import { LocationAnchorSource, useHotelAllLocationAnchorsDeprecatedQuery, useHotelAllLocationAnchorsQuery } from '@graphql/generated';
import { HotelSearchParamsType, PartnerHotelSearchRouteQueryParams } from './HotelSearch.types';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useClientReference } from '@shared/hooks/useClientReference';
import { useCheckInCheckoutDates } from '@shared/hooks/useCheckInCheckoutDates';
import { useHotelBookingRedirectHealth } from '../../HotelBooking.health';
import { useNuiteeLanguage } from '@shared/hooks/useNuiteeLanguage';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { encodeData, XDataInput } from '@shared/utils/encodeData';
import { useSanitizedNuiteeQueryString } from './useSanitizedNuiteeQueryString';
import { useUtmParameters } from '@shared/utils/utmParameters';

export const useHotelSearchParams = (eventHandle?: string) => {
  const queryParamHelper = useQueryParamHelper();
  const { onLoadFailure } = useHotelBookingRedirectHealth();
  const { sanitizeNuiteeQueryString, loading: loadingSanitizeQueryParamFlag } = useSanitizedNuiteeQueryString();
  const utmParamsFromHook = useUtmParameters();

  // Utility function for retrieving query params
  const getQueryParam = (param: PartnerHotelSearchRouteQueryParams) => queryParamHelper.getValueString(param);

  // Retrieve query parameters
  const queryParamPlaceId = getQueryParam(PartnerHotelSearchRouteQueryParams.placeId);
  const queryParamName = getQueryParam(PartnerHotelSearchRouteQueryParams.name);
  const queryParamCheckInDate = getQueryParam(PartnerHotelSearchRouteQueryParams.checkInDate);
  const queryParamCheckOutDate = getQueryParam(PartnerHotelSearchRouteQueryParams.checkOutDate);
  const queryParamEventId = getQueryParam(PartnerHotelSearchRouteQueryParams.eventId);
  // Get UTM params from hook
  const utmParams = utmParamsFromHook || {};

  // Don't need to fetch data, If don't have eventHandle OR already have placeId, checkin & checkout in query param.
  const shouldSkipQuery = !eventHandle || Boolean(queryParamPlaceId && queryParamCheckInDate && queryParamCheckOutDate);
  // Fetch location data via GraphQL
  const {
    data,
    loading: loadingData,
    error: locationsError,
  } = useHotelAllLocationAnchorsQuery({
    variables: { name: eventHandle! },
    batchMode: 'fast',
    skip: shouldSkipQuery,
  });

  // Don't need to fetch data, If don't have eventId OR already have placeId, checkin & checkout in query param.
  const shouldSkipQueryDeprecated = !queryParamEventId || Boolean(queryParamPlaceId && queryParamCheckInDate && queryParamCheckOutDate);
  // @todo - Cleanup with ticket - https://withjoy.atlassian.net/browse/TRAV-622
  const {
    data: dataDeprecated,
    loading: loadingDataDeprecated,
    error: locationsErrorDeprecated,
  } = useHotelAllLocationAnchorsDeprecatedQuery({
    variables: { eventByIdId: queryParamEventId! },
    batchMode: 'fast',
    skip: shouldSkipQueryDeprecated,
  });

  const eventDate = eventHandle ? data?.eventByName?.info.finalizedDate?.dateString : dataDeprecated?.eventById?.info.finalizedDate?.dateString;
  const { checkIn: eventDateCheckIn, checkOut: eventDateCheckOut, message: checkInCheckOutMessage } = useCheckInCheckoutDates({ eventDate });

  // Generate unique client reference
  const { clientReference, loadingClientReference } = useClientReference(queryParamEventId);

  // Get discountCode and build xData, passed to nuitee (stays.withjoy.com)
  const discountCode = getQueryParam(PartnerHotelSearchRouteQueryParams.discountCode);
  const xData = discountCode ? encodeData<XDataInput>({ discount: { code: discountCode } }) : undefined;

  // Get currency and language
  const language = useNuiteeLanguage();
  const currency = useTravelWhiteLabelCurrency();

  // Default values for location
  let placeId = '';
  let name = '';
  let locationSource = '';

  // Get location anchors
  const locationAnchors = eventHandle ? (data?.eventByName?.allLocationAnchors ?? []) : (dataDeprecated?.eventById?.allLocationAnchors ?? []);
  const venueLocation = locationAnchors.find(location => location?.source === LocationAnchorSource.event_venue);
  const centroidLocation = locationAnchors.find(location => location?.source === LocationAnchorSource.approximated_centroid);
  const eventLocation = locationAnchors.find(location => location?.source === LocationAnchorSource.event_location);

  const loading = loadingData || loadingDataDeprecated || loadingClientReference || loadingSanitizeQueryParamFlag;

  let locationMessage = 'Location: ';

  const checkin = queryParamCheckInDate || eventDateCheckIn;
  const checkout = queryParamCheckOutDate || eventDateCheckOut;

  // Determine location selection
  if (queryParamPlaceId) {
    placeId = queryParamPlaceId;
    name = queryParamName || '';
    locationSource = 'queryParam';
    locationMessage += `Found queryParamPlaceId - `;
  } else if (venueLocation) {
    placeId = venueLocation.googlePlaceID;
    name = venueLocation.friendlyName;
    locationSource = venueLocation.source;
    locationMessage += `Found venue - `;
  } else if (centroidLocation || eventLocation) {
    const location = eventLocation ?? centroidLocation;
    placeId = location?.googlePlaceID!;
    name = location?.friendlyName!;
    locationSource = location?.source!;
    locationMessage += `Not found venue, No experiment - `;
  } else {
    locationMessage += `Not found venue, No experiment, No Location.`;
  }

  locationMessage += `placeId: ${placeId}, name: ${name}, locationSource: ${locationSource}`;

  // Prepare the search params object
  const params: HotelSearchParamsType = {
    clientReference,
    placeId,
    locationSource,
    name,
    checkin,
    checkout,
    currency,
    language,
    xData,
    ...utmParams,
  };

  // Function to build redirect URL
  const buildRedirectUrl = ({ url, fields }: { url: string; fields: (keyof HotelSearchParamsType)[] }) => {
    // eslint-disable-next-line no-console
    console.log(checkInCheckOutMessage);
    // eslint-disable-next-line no-console
    console.log(locationMessage);

    // Collect error messages
    const errors = [
      (locationsError || locationsErrorDeprecated) && 'GraphQL query failed to fetch AllLocationAnchors data',
      !queryParamEventId && !eventHandle && 'eventId and eventHandle Missing',
      !placeId && 'PlaceId Missing',
      !name && 'Name Missing',
      !utmParams.utm_source && 'Source Missing',
    ].filter(Boolean) as string[];

    // Report if any errors exist
    const combinedError = errors.join('\n');
    if (combinedError) {
      onLoadFailure(`HotelSearch: ${combinedError}`);
    }

    const redirectUrl = new URL(url);
    fields.forEach(field => {
      const fieldValue = params[field];
      if (fieldValue != null) {
        redirectUrl.searchParams.append(field, field === 'name' ? sanitizeNuiteeQueryString(fieldValue.toString()) : fieldValue.toString());
      }
    });

    return redirectUrl;
  };

  return { ...params, loading, buildRedirectUrl };
};
